import { useMemo } from 'react';
import { isColumnGroup } from '../../domain';
import type {
    ColumnDef,
    PersistedLayout,
    ResolvedColumn,
    TableColumn,
} from '../../domain';
import { builtInColumnTypes } from '../../domain/schema/column-types';
import { readPath } from '../../domain/shared/path';
import type { Capabilities } from '../../ports';
import { COLUMN_DEFAULTS, STRUCTURAL_IDS } from '../../defaults';

function builtInTypesFor<T>(): Record<string, Partial<TableColumn<T>>> {
    return builtInColumnTypes as Record<string, Partial<TableColumn<T>>>;
}

export interface UseColumnsArgs<T> {
    defs: ColumnDef<T>[];
    caps: Capabilities;
    layout: PersistedLayout;
}

interface FlatEntry<T> {
    col: TableColumn<T>;
    groupHeader?: string;
    groupId?: string;
}

function splitCamel(word: string): string[] {
    return word
        .replace(/(?<lower>[a-z0-9])(?<upper>[A-Z])/gu, '$<lower> $<upper>')
        .split(' ');
}

function humaniseField(source: string): string {
    const segments = source.split('.');
    const lastWords = splitCamel(segments[segments.length - 1]);
    const words =
        segments.length > 1
            ? [
                  splitCamel(segments[segments.length - 2]).slice(-1)[0],
                  ...lastWords,
              ]
            : lastWords;
    const label = words.map((w) => w.toLowerCase()).join(' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
}

function flattenDefs<T>(defs: ColumnDef<T>[]): FlatEntry<T>[] {
    const out: FlatEntry<T>[] = [];
    defs.forEach((def) => {
        if (isColumnGroup(def)) {
            def.columns.forEach((col) => {
                out.push({ col, groupHeader: def.header, groupId: def.id });
            });
        } else {
            out.push({ col: def });
        }
    });
    return out;
}

function resolveColumn<T>(
    entry: FlatEntry<T>,
    caps: Capabilities,
): ResolvedColumn<T> {
    const { col, groupHeader, groupId } = entry;
    const builtIn = col.type ? builtInTypesFor<T>()[col.type] : undefined;
    const merged: TableColumn<T> = { ...COLUMN_DEFAULTS, ...builtIn, ...col };

    const id = merged.id ?? merged.field;
    if (!id)
        throw new Error(
            'A column needs an `id` or a `field` — persistence depends on it.',
        );

    const header = merged.header ?? humaniseField(merged.field ?? id);
    const hasData = merged.field !== undefined || merged.value !== undefined;
    const getValue = (row: T): unknown =>
        merged.value ? merged.value(row) : readPath(row, merged.field ?? '');

    let canSort = caps.sort
        ? Boolean(merged.sortKey)
        : merged.sortable !== false;
    let canFilter = caps.filter
        ? Boolean(merged.filter && merged.filterKey)
        : Boolean(merged.filter);
    if (
        caps.filter &&
        merged.filter === 'set' &&
        !merged.filterOptions?.length
    ) {
        canFilter = false;
    }
    if (!hasData) {
        canSort = false;
        canFilter = false;
    }

    return {
        ...merged,
        id,
        header,
        groupHeader,
        groupId,
        canSort,
        canFilter,
        getValue,
    };
}

export function resolveAllColumns<T>(
    defs: ColumnDef<T>[],
    caps: Capabilities,
): ResolvedColumn<T>[] {
    return flattenDefs(defs).map((entry) => resolveColumn(entry, caps));
}

function isHidden<T>(col: ResolvedColumn<T>, layout: PersistedLayout): boolean {
    const choice = layout.columnVisibility?.[col.id];
    return choice === undefined ? Boolean(col.hidden) : !choice;
}

export function applyOrder<T>(
    cols: ResolvedColumn<T>[],
    order?: string[],
): ResolvedColumn<T>[] {
    if (!order) return cols;
    const byId = new Map(cols.map((c) => [c.id, c]));
    const listed = order.flatMap((id) => byId.get(id) ?? []);
    const listedIds = new Set(order);
    const rest = cols.filter((c) => !listedIds.has(c.id));
    return [...listed, ...rest];
}

function wouldHideAllNonStructural<T>(
    cols: ResolvedColumn<T>[],
    layout: PersistedLayout,
): boolean {
    const nonStructural = cols.filter((c) => !STRUCTURAL_IDS.includes(c.id));
    if (nonStructural.length === 0) return false;
    return nonStructural.every((c) => isHidden(c, layout));
}

function applyLayout<T>(
    cols: ResolvedColumn<T>[],
    layout: PersistedLayout,
): ResolvedColumn<T>[] {
    const ignorePersistedVisibility = wouldHideAllNonStructural(cols, layout);
    const visible = cols.filter((c) =>
        ignorePersistedVisibility ? !c.hidden : !isHidden(c, layout),
    );
    const ordered = applyOrder(visible, layout.columnOrder);
    return ordered.map((c) => {
        const width = layout.columnWidths?.[c.id];
        return width === undefined ? c : { ...c, width };
    });
}

export function resolveColumns<T>(
    args: UseColumnsArgs<T>,
): ResolvedColumn<T>[] {
    const { defs, caps, layout } = args;
    return applyLayout(resolveAllColumns(defs, caps), layout);
}

export function useColumns<T>(args: UseColumnsArgs<T>): ResolvedColumn<T>[] {
    const { defs, caps, layout } = args;
    return useMemo(
        () => resolveColumns({ defs, caps, layout }),
        [defs, caps, layout],
    );
}

export function allColumns<T>(defs: ColumnDef<T>[]): ResolvedColumn<T>[] {
    return resolveAllColumns(defs, {});
}
